import React, {Fragment} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {useTranslation} from 'react-i18next';
import * as Papa from 'papaparse';
import CircularProgress from "@material-ui/core/CircularProgress";
import {palette} from "../../layout/_constants";
import HelpOutline from "@material-ui/icons/HelpOutline";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import {Checkbox, FormControlLabel} from "@material-ui/core";
import CSVHelper from "./CSVHelper";
import useLocalStorage from "./useLocalStorage";

const useStyles = makeStyles(theme => ({
    label: {
        color: palette.primary.main,
        fontSize: '1rem',
        '&:hover': {
            textDecoration: 'underline'
        }
    },
    checkboxDontShowAgain: {
        fontSize: '.9rem',
    },
}));

interface CSVHelperButtonProps {
    format: any
}

const CSVHelperButton: React.FC<CSVHelperButtonProps> = ({format}) => {
    const [openHelper, setOpenHelper] = React.useState(false);

    const handleOpenHelper = () => {
        setOpenHelper(true);
    };

    const handleCloseHelper = () => {
        setOpenHelper(false);
    };

    const csvHelperAction = (csvHelperActions: any) =>
        <Grid container alignItems="center" spacing={2} justify="flex-end">
            <Grid item>
                {csvHelperActions}
            </Grid>
            <Button autoFocus onClick={() => setOpenHelper(false)} color="primary">
                OK
            </Button>
        </Grid>

    return <Fragment>
        <CSVHelper open={openHelper}
                   content={format}
                   renderAction={csvHelperAction}
                   handleClose={handleCloseHelper}/>
        <IconButton color="primary" onClick={handleOpenHelper}>
            <HelpOutline/>
        </IconButton>
    </Fragment>
}

interface CSVImportProps {
    displayedLabel: string,
    format: {
        headers: string[],
        examples: string[][]
    },
    onError: (msg: string, arg?: any) => void,
    onComplete: (data: any[]) => void
}

const CSVImport: React.FC<CSVImportProps> = ({displayedLabel, format, onError, onComplete}) => {
    const classes = useStyles();
    const {t} = useTranslation();
    const [openHelper, setOpenHelper] = React.useState(false);
    const [dontShowAgain, setDontShowAgain] = useLocalStorage('csvHelper-DontShowAgain', false);

    let showSpinner = false;

    const handleDontShowAgain = (checked: boolean) => {
        setDontShowAgain(checked)
    }

    const handleOpenHelper = () => {
        if (!dontShowAgain)
            setOpenHelper(true);
    };

    const handleCloseHelper = () => {
        setOpenHelper(false);
    };

    const headerValidation = (headers: string[]): boolean => {
        return format.headers.every((header: string) => headers.some(h => {
            const headerLowerCase = h.trim().toLowerCase();
            return headerLowerCase === t(`_Common.Labels.${header}`).trim().toLowerCase()
        }))
    }

    const handleChangeFile = (e: any) => {
        const input = e.target;
        const file: File = input?.files?.length > 0 ? input.files[0] : null;
        showSpinner = true;
        let dataOrdered: any[] = [];

        if (!file)
            return;

        let config = {
            complete: ((result: any) => {
                if (!result.data || !result.data.length)
                    return onComplete([]);

                if (headerValidation(result.data[0])) {
                    const headerCSV = result.data.shift();
                    const headerOrder: any[] = headerCSV
                        .map((col: string, i: number) => ({
                            csvOrder: i,
                            formatOrder: format.headers.findIndex((h: string) => col.trim().toLowerCase() === t(`_Common.Labels.${h}`).trim().toLowerCase())
                        }))
                        .filter((h: any) => h.formatOrder >= 0)

                    for (let j = 0; j < result.data.length; j++) {
                        let row = Array(format.headers.length);
                        for (let i = 0; i < headerOrder.length; i++) {
                            row[headerOrder[i].formatOrder] = result.data[j][headerOrder[i].csvOrder].trimStart().trimEnd();
                        }
                        dataOrdered.push(row);
                    }
                } else
                    return onError(t('_Shared.CSV.Error.ColumnsMismatch'));

                showSpinner = false;
                input.value = null;
                return onComplete(dataOrdered);
            }),
            error: ((error: any) => {
                showSpinner = false;
                input.value = null;
                return onError(t('_Shared.CSV.Error.OnLoad'), error.code);
            }),
            skipEmptyLines: true
        };
        Papa.parse(file, config);
        setOpenHelper(false);
    };

    const fileInput = <input type="file" id="contained-button-file"
                             onClick={e => (e.target as any).value = null}
                             onChange={e => handleChangeFile(e)} style={{display: "none"}}/>;

    const csvHelperAction = (csvHelperActions: any) =>
        <Grid container alignItems="center" justify="space-between">
            <Grid item>
                <FormControlLabel classes={{label: classes.checkboxDontShowAgain}}
                                  control={<Checkbox
                                      size="small"
                                      color="primary"
                                      inputProps={{'aria-label': 'primary checkbox'}}
                                      onChange={e => handleDontShowAgain(e.target.checked)}
                                  />}
                                  label={t('_Common.Labels.DontShowAgain')}
                />
            </Grid>
            <Grid item>
                <Grid container alignItems="center" spacing={2}>
                    <Grid item>
                        {csvHelperActions}
                    </Grid>
                    <Grid item>
                        {fileInput}
                        <label htmlFor="contained-button-file">
                            <Button color="primary" component="span" variant="contained">
                                {t('_Common.Labels.CSV')}
                            </Button>
                        </label>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>

    return (
        <Grid container alignItems="center">
            <label className={classes.label} onClick={handleOpenHelper}>
                {'+ ' + displayedLabel}
                {
                    dontShowAgain && fileInput
                }
            </label>
            {showSpinner && <CircularProgress size={20} color="inherit"/>}
            <CSVHelperButton format={format}/>
            <CSVHelper open={openHelper}
                       content={format}
                       renderAction={csvHelperAction}
                       handleClose={handleCloseHelper}/>
        </Grid>);
};

export default CSVImport;
